/**
 * Readers for the free-shape `Bid.insights` blob the Upwork sync writes (the
 * proposal's insights from `list_freelancer_proposals action=get`). Observed in
 * the 2026-10-12 backfill — see docs/crm.md §Bids:
 *   proposals_total · proposals_opened · proposals_unopened · shortlisted · messaged
 *   applicants_avg_jss (0–1) · applicants_avg_earnings (USD) · applicants_avg_completed_jobs
 *   applicants_avg_years_on_upwork · applicants_top_skills | common_skills · computed_at
 * Skills come as names or as `{skill, applicants}`; `opened` is the short form
 * used by hand-entered rows. Keep this list to spellings actually seen.
 */

export type Insights = Record<string, unknown>;

export const INSIGHT_KEYS = {
  total: ['proposals_total'],
  opened: ['proposals_opened', 'opened'],
  unopened: ['proposals_unopened'],
  shortlisted: ['shortlisted'],
  messaged: ['messaged'],
  jss: ['applicants_avg_jss'],
  earnings: ['applicants_avg_earnings'],
  jobs: ['applicants_avg_completed_jobs'],
  years: ['applicants_avg_years_on_upwork'],
  skills: ['applicants_top_skills', 'common_skills'],
  computedAt: ['computed_at'],
} as const;

const isEmpty = (v: unknown) =>
  v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}

/**
 * Reads insight values and remembers which keys it actually rendered, so the
 * caller can show every other key (unknown, unparsable, or a shadowed alias)
 * as a plain row rather than dropping it.
 */
export function insightReader(src: Insights) {
  const used = new Set<string>();
  /** The first alias whose value parses — an empty or unparsable one falls
   *  through to the next, and stays unused (so it still shows under "More"). */
  function firstUsable<T>(keys: readonly string[], parse: (v: unknown) => T | null): T | null {
    for (const k of keys) {
      if (src[k] === undefined || src[k] === null) continue;
      const value = parse(src[k]);
      if (value !== null) {
        used.add(k);
        return value;
      }
    }
    return null;
  }
  return {
    number: (keys: readonly string[]): number | null => firstUsable(keys, num),
    skills: (keys: readonly string[]): SkillCount[] =>
      firstUsable(keys, (v) => {
        const list = normaliseSkills(v);
        return list.length > 0 ? list : null;
      }) ?? [],
    text: (keys: readonly string[]): string | null =>
      firstUsable(keys, (v) => (typeof v === 'string' ? v : null)),
    /** Entries nothing above rendered — shown under "More" (empty ones skipped). */
    rest(): [string, unknown][] {
      return Object.entries(src).filter(([k, v]) => !used.has(k) && !isEmpty(v));
    },
  };
}

export interface SkillCount {
  skill: string;
  applicants: number | null;
}

/** Skills arrive as names (`["MERN Stack", …]`) or counts (`[{skill, applicants}]`);
 *  a repeated name is kept once. */
export function normaliseSkills(v: unknown): SkillCount[] {
  if (!Array.isArray(v)) return [];
  const seen = new Set<string>();
  return v.flatMap((item): SkillCount[] => {
    let entry: SkillCount | null = null;
    if (typeof item === 'string' && item.trim()) entry = { skill: item.trim(), applicants: null };
    else if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      const skill = o.skill ?? o.name;
      if (typeof skill === 'string' && skill.trim()) {
        entry = { skill: skill.trim(), applicants: num(o.applicants ?? o.count) };
      }
    }
    if (!entry || seen.has(entry.skill)) return [];
    seen.add(entry.skill);
    return [entry];
  });
}
